import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatMoney, resolveCurrencyCode } from '@/lib/money'
import * as analyticsService from '../../services/analyticsService'
import { useCurrentStore } from '../../context/CurrentStoreContext'
import { RevenueBarChart, StatusBarList } from '../../components/analytics/AnalyticsCharts'
import { Card, CardHeader } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { SkeletonCard, SkeletonListPage } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/ui/EmptyState'
import { getUserFacingErrorMessage } from '../../utils/userFacingError'

const PERIOD_OPTIONS: { value: analyticsService.AnalyticsPeriod; label: string }[] = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
]

export default function AnalyticsPage() {
  const { currentStoreId: storeId } = useCurrentStore()
  const [period, setPeriod] = useState<analyticsService.AnalyticsPeriod>(30)
  const [overview, setOverview] = useState<analyticsService.StoreAnalyticsOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [exportFrom, setExportFrom] = useState('')
  const [exportTo, setExportTo] = useState('')
  const [exportingOrders, setExportingOrders] = useState(false)
  const [exportingTransactions, setExportingTransactions] = useState(false)

  useEffect(() => {
    const range = analyticsService.periodToIsoRange(period)
    setExportFrom(analyticsService.isoDateInputValue(range.from))
    setExportTo(analyticsService.isoDateInputValue(range.to))
  }, [period])

  const loadOverview = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await analyticsService.fetchAnalyticsOverview({
        period,
        storeId: storeId ?? undefined,
      })
      setOverview(data)
    } catch (e) {
      setOverview(null)
      setError(getUserFacingErrorMessage(e, 'Failed to load analytics'))
    } finally {
      setLoading(false)
    }
  }, [period, storeId])

  useEffect(() => {
    void loadOverview()
  }, [loadOverview])

  async function handleExportOrders() {
    if (!exportFrom || !exportTo) return
    setExportingOrders(true)
    setError(null)
    try {
      await analyticsService.exportOrdersCsv({
        from: analyticsService.dateInputToIsoStart(exportFrom),
        to: analyticsService.dateInputToIsoEnd(exportTo),
        storeId: storeId ?? undefined,
      })
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to export orders'))
    } finally {
      setExportingOrders(false)
    }
  }

  async function handleExportTransactions() {
    if (!exportFrom || !exportTo) return
    setExportingTransactions(true)
    setError(null)
    try {
      await analyticsService.exportTransactionsCsv({
        from: analyticsService.dateInputToIsoStart(exportFrom),
        to: analyticsService.dateInputToIsoEnd(exportTo),
        storeId: storeId ?? undefined,
      })
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to export transactions'))
    } finally {
      setExportingTransactions(false)
    }
  }

  const currency = resolveCurrencyCode(overview?.currency)
  const hasActivity =
    (overview?.totalRevenue ?? 0) > 0 ||
    (overview?.totalOrders ?? 0) > 0 ||
    (overview?.topProducts.length ?? 0) > 0

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Analytics"
          description="Revenue trends, order breakdowns, and top products for accounting and inventory planning."
        />
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">Period</span>
            <select
              className="min-w-[160px] rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              value={period}
              disabled={loading}
              onChange={(e) => setPeriod(Number(e.target.value) as analyticsService.AnalyticsPeriod)}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {error ? <p className="mt-4 text-sm text-destructive">{error}</p> : null}
      </Card>

      {loading ? (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <SkeletonCard />
            <SkeletonCard />
          </div>
          <SkeletonCard />
          <SkeletonListPage toolbarFields={0} columns={4} rows={5} />
        </div>
      ) : !hasActivity ? (
        <EmptyState
          title="No sales data in this period"
          description="When customers place orders or you record in-store sales, charts and exports will appear here."
          action={
            <Link to="/orders" className="text-sm font-medium text-primary hover:underline">
              View orders
            </Link>
          }
        />
      ) : overview ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <Card className="p-5" padding={false}>
              <p className="text-sm font-medium text-muted-foreground">Total revenue</p>
              <p className="mt-2 text-3xl font-semibold tabular-nums tracking-tight">
                {formatMoney(overview.totalRevenue, currency)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Online orders + in-store POS</p>
            </Card>
            <Card className="p-5" padding={false}>
              <p className="text-sm font-medium text-muted-foreground">Orders</p>
              <p className="mt-2 text-3xl font-semibold tabular-nums tracking-tight">{overview.totalOrders}</p>
              <p className="mt-1 text-xs text-muted-foreground">Excludes cancelled</p>
            </Card>
          </div>

          <Card className="pb-5">
            <CardHeader title="Revenue over time" description="Daily totals for the selected period." />
            <RevenueBarChart buckets={overview.revenueOverTime.buckets} currency={overview.currency} />
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader title="Orders by status" description="Breakdown of order states in this period." />
              <StatusBarList items={overview.ordersByStatus} />
            </Card>
            <Card>
              <CardHeader title="Top products" description="By revenue (online + POS)." />
              {overview.topProducts.length === 0 ? (
                <p className="text-sm text-muted-foreground">No product sales in this period.</p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="min-w-full divide-y divide-border text-sm">
                    <thead className="bg-muted/30 text-left text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3 font-medium">Product</th>
                        <th className="px-4 py-3 font-medium">Units</th>
                        <th className="px-4 py-3 font-medium">Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border bg-card">
                      {overview.topProducts.map((product) => (
                        <tr key={product.productId}>
                          <td className="px-4 py-3">
                            <Link
                              to={`/products/${product.productId}`}
                              className="font-medium text-primary hover:underline"
                            >
                              {product.name}
                            </Link>
                            {product.sku ? (
                              <p className="text-xs text-muted-foreground">{product.sku}</p>
                            ) : null}
                          </td>
                          <td className="px-4 py-3 tabular-nums">{product.unitsSold}</td>
                          <td className="px-4 py-3 tabular-nums">
                            {formatMoney(product.revenue, currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        </>
      ) : null}

      <Card>
        <CardHeader
          title="Export CSV"
          description="Download orders or harmonized transactions for a custom date range (for accounting reconciliation)."
        />
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">From</span>
            <Input type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">To</span>
            <Input type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)} />
          </label>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={!exportFrom || !exportTo || exportingOrders}
            onClick={() => void handleExportOrders()}
          >
            {exportingOrders ? 'Exporting…' : 'Export orders'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={!exportFrom || !exportTo || exportingTransactions}
            onClick={() => void handleExportTransactions()}
          >
            {exportingTransactions ? 'Exporting…' : 'Export transactions'}
          </Button>
        </div>
      </Card>
    </div>
  )
}
